import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Card, CardContent, Typography, IconButton, Tooltip } from '@mui/material';
import type { ReactNode, FC } from 'react';

interface MetricCardProps {
  title: string;
  value: React.ReactNode;
  description: string;
  type: 'orders' | 'revenue' | 'average' | 'conversion';
}

const getCardStyles = (
  type: MetricCardProps['type']
): { icon: ReactNode; gradient: string } => {
  const styles = {
    orders: {
      icon: <ShoppingCartIcon />,
      gradient: 'linear-gradient(135deg, #6B8DD6 0%, #8E37D7 100%)',
    },
    revenue: {
      icon: <MonetizationOnIcon />,
      gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
    },
    average: {
      icon: <ReceiptIcon />,
      gradient: 'linear-gradient(135deg, #FF8F00 0%, #FFC107 100%)',
    },
    conversion: {
      icon: <TrendingUpIcon />,
      gradient: 'linear-gradient(135deg, #E53935 0%, #FF5252 100%)',
    },
  };
  return styles[type];
};

const MetricCard: FC<MetricCardProps> = ({ title, value, description, type }) => {
  const styles = getCardStyles(type);

  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: styles.gradient,
          opacity: 0.08,
        },
      }}>
      <CardContent>
        <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
          <Box display='flex' alignItems='center' gap={1}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                background: styles.gradient,
                color: 'white',
                display: 'flex',
              }}>
              {styles.icon}
            </Box>
            <Typography color='text.secondary' variant='subtitle2'>
              {title}
            </Typography>
          </Box>
          <Tooltip title={description}>
            <IconButton size='small'>
              <InfoOutlinedIcon fontSize='inherit' />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant='h4' component='div'>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default MetricCard;
